import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { TextInput, useNotify, useRecordContext } from "react-admin";
import { useFormContext } from "react-hook-form";
import { callApi } from "@/dataprovider/misc-apis";
import { apiErrorMessage } from "../shared/api-error";
import {
  applyItsPrefill,
  describeItsLookup,
  ITS_ID_PATTERN,
  type ItsLookupResult,
  type LookupNote,
} from "./student-form-utils";

/**
 * ITS field with a directory lookup. The lookup only fills fields that are still empty and says
 * what it did, so it can never silently replace something the user typed.
 */
export function ItsLookupInput() {
  const record = useRecordContext<{ id?: string }>();
  const notify = useNotify();
  const { getValues, setValue, watch } = useFormContext();
  const itsId = String(watch("itsId") ?? "").trim();
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState<LookupNote[]>([]);

  // Notes describe a specific ITS; drop them as soon as the user changes it.
  useEffect(() => {
    setNotes([]);
  }, [itsId]);

  const handleLookup = async () => {
    setBusy(true);
    try {
      const response = await callApi({
        location: `madrasaStudents/its-lookup/${itsId}`,
        method: "GET",
      });
      const lookup = response.data as ItsLookupResult;
      const outcome = applyItsPrefill(
        {
          name: getValues("name"),
          gender: getValues("gender"),
          address: getValues("address"),
          guardian1Name: getValues("guardian1Name"),
          guardian1Phone: getValues("guardian1Phone"),
        },
        lookup
      );
      for (const [field, value] of Object.entries(outcome.updates)) {
        setValue(field, value, { shouldDirty: true, shouldValidate: true });
      }
      setNotes(describeItsLookup(lookup, outcome, record?.id));
    } catch (error: unknown) {
      notify(apiErrorMessage(error, "ITS lookup failed"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <TextInput
          source="itsId"
          label="ITS ID"
          helperText="8-digit ITS number. Does not need to be in the directory."
          isRequired
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          onClick={handleLookup}
          disabled={busy || !ITS_ID_PATTERN.test(itsId)}
          sx={{ mt: 1 }}
        >
          {busy ? "Looking up…" : "Look up ITS"}
        </Button>
      </Box>
      {notes.map((note) => (
        <Alert key={note.message} severity={note.severity} sx={{ mb: 1 }}>
          {note.message}
        </Alert>
      ))}
    </Box>
  );
}
